// src/components/blog/components/FloatingShare.tsx

'use client'

import { motion, AnimatePresence } from 'motion/react'
import { useState } from 'react'
import { 
  CopyIcon, 
  CheckIcon 
} from 'lucide-react'
import RiLinkedinFill from '~/icons/remix/generated/RiLinkedinFill'
import RiTwitterXFill from '~/icons/remix/generated/RiTwitterXFill'

interface FloatingShareProps {
  title: string
  url: string
}

export function FloatingShare({ title, url }: FloatingShareProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const shareLinks = [
    {
      name: 'Twitter',
      icon: RiTwitterXFill,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`
    },
    {
      name: 'LinkedIn',
      icon: RiLinkedinFill,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    }
  ]

  const copyToClipboard = async (): Promise<void> => {
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed bottom-8 right-8 z-50 hidden sm:block">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="mb-4 flex flex-col gap-2"
          >
            {shareLinks.map((link) => (
              <motion.a
                key={link.name}
                href={link.href}
                aria-label={`Share on ${link.name === 'Twitter' ? 'X' : link.name} (opens in new tab)`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg dark:bg-gray-800"
              >
                <link.icon aria-hidden="true" className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </motion.a>
            ))}
            <motion.button
              whileHover={{ scale: 1.1 }}
              onClick={copyToClipboard}
              aria-label={copied ? 'Article link copied' : 'Copy article link'}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg dark:bg-gray-800"
            >
              {copied ? (
                <CheckIcon className="h-5 w-5 text-green-500" />
              ) : (
                <CopyIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              )}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close sharing options' : 'Share this article'}
        aria-expanded={isOpen}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg"
      >
        <svg
          aria-hidden="true"
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
          />
        </svg>
      </motion.button>
    </div>
  )
}
